import {
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PERSONA_STAGE_TOOLBAR_CLASS } from "../../../../lib/persona-opportunity-stage-chrome.js";
import type {
  PersonaOpportunityGroupBy,
  PersonaOpportunityStatusFilter,
  PersonaOpportunityViewMode,
} from "../../../../types/persona-opportunities.types.js";
import { PersonaOpportunitySelectionActionsMenu } from "./PersonaOpportunitySelectionActionsMenu.js";

export interface PersonaOpportunityOpportunitiesToolbarProps {
  personaCount: number;
  angleCount: number;
  allExpanded: boolean;
  onToggleExpandAll: () => void;
  view: PersonaOpportunityViewMode;
  onViewChange: (view: PersonaOpportunityViewMode) => void;
  search: string;
  onSearchChange: (value: string) => void;
  status: PersonaOpportunityStatusFilter;
  onStatusChange: (value: PersonaOpportunityStatusFilter) => void;
  groupBy: PersonaOpportunityGroupBy;
  onGroupByChange: (value: PersonaOpportunityGroupBy) => void;
  hidePersonaGroup?: boolean;
  selectedCount: number;
  canGenerateScripts: boolean;
  isGeneratingScripts: boolean;
  onClearSelection: () => void;
  onGenerateScripts: () => void;
}

const VIEWS: { value: PersonaOpportunityViewMode; icon: "rows-3" | "layout-grid" }[] = [
  { value: "table", icon: "rows-3" },
  { value: "cards", icon: "layout-grid" },
];

const GROUP_BY_OPTIONS: {
  value: PersonaOpportunityGroupBy;
  labelKey: string;
  fallback: string;
}[] = [
  { value: "persona", labelKey: "personaOpportunities.active.groupBy.persona", fallback: "Persona" },
  { value: "angle", labelKey: "personaOpportunities.active.groupBy.angle", fallback: "Angle" },
  { value: "set", labelKey: "personaOpportunities.active.groupBy.set", fallback: "Set" },
];

const STATUS_OPTIONS: { value: PersonaOpportunityStatusFilter; labelKey: string; fallback: string }[] =
  [
    { value: "all", labelKey: "personaOpportunities.active.allStatuses", fallback: "All Statuses" },
    { value: "NEW", labelKey: "personaOpportunities.active.statusNew", fallback: "New" },
    {
      value: "SCRIPTS_GENERATED",
      labelKey: "personaOpportunities.active.statusScriptsGenerated",
      fallback: "Scripts generated",
    },
    {
      value: "BRIEFS_GENERATED",
      labelKey: "personaOpportunities.active.statusBriefsGenerated",
      fallback: "Briefs generated",
    },
    {
      value: "SCRIPTS_AND_BRIEFS_GENERATED",
      labelKey: "personaOpportunities.active.statusScriptsAndBriefsGenerated",
      fallback: "Scripts + briefs generated",
    },
    {
      value: "SENT_TO_VENDOR",
      labelKey: "personaOpportunities.active.statusSentToVendor",
      fallback: "Sent to production partner",
    },
  ];

export function PersonaOpportunityOpportunitiesToolbar({
  personaCount,
  angleCount,
  allExpanded,
  onToggleExpandAll,
  view,
  onViewChange,
  search,
  onSearchChange,
  status,
  onStatusChange,
  groupBy,
  onGroupByChange,
  hidePersonaGroup = false,
  selectedCount,
  canGenerateScripts,
  isGeneratingScripts,
  onClearSelection,
  onGenerateScripts,
}: PersonaOpportunityOpportunitiesToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className={PERSONA_STAGE_TOOLBAR_CLASS}>
      <div className="flex flex-wrap items-center gap-3 text-[13px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <Icon name="users" size="xs" className="text-muted-foreground" aria-hidden />
          {t("personaOpportunities.active.summary", {
            personas: personaCount,
            angles: angleCount,
            defaultValue: `${personaCount} personas · ${angleCount} angles`,
          })}
        </span>
        <button
          type="button"
          onClick={onToggleExpandAll}
          className="text-[13px] font-medium text-primary hover:underline"
        >
          {allExpanded
            ? t("personaOpportunities.active.collapseAll", "Collapse all")
            : t("personaOpportunities.active.expandAll", "Expand all")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("personaOpportunities.active.searchPlaceholder", "Search angles")}
          className="h-8 min-w-[10rem] flex-1 sm:flex-none"
          aria-label={t("personaOpportunities.active.searchPlaceholder", "Search angles")}
        />

        <Select
          value={status}
          onValueChange={(value) => onStatusChange(value as PersonaOpportunityStatusFilter)}
        >
          <SelectTrigger
            className="h-8 w-[16rem]"
            aria-label={t("personaOpportunities.active.allStatuses", "All Statuses")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t(option.labelKey, option.fallback)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={groupBy}
          onValueChange={(value) => onGroupByChange(value as PersonaOpportunityGroupBy)}
        >
          <SelectTrigger
            className="h-8 w-[12rem]"
            aria-label={t("personaOpportunities.active.groupBy.label", "Group by")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_BY_OPTIONS.filter((option) => !hidePersonaGroup || option.value !== "persona").map(
              (option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.labelKey, option.fallback)}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        <PersonaOpportunitySelectionActionsMenu
          selectedCount={selectedCount}
          isGenerating={isGeneratingScripts}
          canGenerateScripts={canGenerateScripts}
          onClearSelection={onClearSelection}
          onGenerateScripts={onGenerateScripts}
        />

        <TooltipProvider delayDuration={400}>
          <div
            className="inline-flex items-center rounded-md border border-primary/30 shadow-xs dark:border-primary/40"
            role="group"
            aria-label={t("personaOpportunities.active.viewToggleAria", "Switch view")}
          >
            {VIEWS.map((entry) => (
              <Tooltip key={entry.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onViewChange(entry.value)}
                    aria-pressed={view === entry.value}
                    aria-label={t(`personaOpportunities.active.view.${entry.value}`)}
                    className={cn(
                      "inline-flex min-h-11 min-w-11 items-center justify-center text-sm transition-colors first:rounded-l-md last:rounded-r-md [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8",
                      view === entry.value
                        ? "bg-primary/10 text-primary dark:bg-primary/20"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    <Icon name={entry.icon} size="xs" color="inherit" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {t(`personaOpportunities.active.view.${entry.value}`)}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
}
